<template>
  <SidebarProvider class="bg-muted">
    <AppSidebar />
    <SidebarInset class="bg-background border m-4 ms-1 rounded-lg h-[calc(100vh-32px)] overflow-hidden relative">
      <div
        v-if="isModuleDisabled"
        class="absolute top-0 left-0 w-full h-full bg-secondary/95 blur-3 z-50 flex items-center justify-center rounded-lg"
      >
        <h4>
          This module is currently disable, please contact <a
            class="text-primary"
            href="mailto:support@complyone.tech"
          >support@complyone.tech</a> for more
          information.
        </h4>
      </div>
      <header
        class="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12"
      >
        <div class="flex items-center w-full gap-3 px-4">
          <!-- <SidebarTrigger/> -->
          <!-- <CTooltip title="Go to previous page"> -->
          <Button
            v-if="hasPreviousRoute"
            size="xs"
            variant="ghost"
            title="Go to previous page"
            @click="goBack"
          >
            <ArrowLeft />
          </Button>
          <!-- </CTooltip> -->
          <div v-can="['admin']">
            <Breadcrumb v-if="breadcrumbs.length <= MAX_VISIBLE">
              <BreadcrumbList>
                <template
                  v-for="(crumb, index) in breadcrumbs"
                  :key="index"
                >
                  <BreadcrumbItem>
                    <RouterLink
                      v-if="index !== breadcrumbs.length - 1"
                      :to="crumb.path"
                    >
                      <BreadcrumbLink>
                        {{ crumb.title }}
                      </BreadcrumbLink>
                    </RouterLink>

                    <BreadcrumbPage v-else>
                      {{ crumb.title }}
                    </BreadcrumbPage>
                  </BreadcrumbItem>

                  <BreadcrumbSeparator
                    v-if="index !== breadcrumbs.length - 1"
                    class="hidden md:block"
                  />
                </template>
              </BreadcrumbList>
            </Breadcrumb>

            <Breadcrumb v-else>
              <BreadcrumbList>
                <template
                  v-for="(crumb, index) in breadcrumbDisplay.start"
                  :key="index"
                >
                  <BreadcrumbItem>
                    <RouterLink
                      v-if="index !== breadcrumbs.length - 1"
                      :to="crumb.path"
                    >
                      <BreadcrumbLink>
                        {{ crumb.title }}
                      </BreadcrumbLink>
                    </RouterLink>

                    <BreadcrumbPage v-else>
                      {{ crumb.title }}
                    </BreadcrumbPage>
                  </BreadcrumbItem>

                  <BreadcrumbSeparator class="hidden md:block" />
                </template>

                <BreadcrumbItem>
                  <DropdownMenu>
                    <DropdownMenuTrigger as-child>
                      <Button
                        size="xs"
                        variant="ghost"
                        class="px-0"
                        aria-label="Show hidden breadcrumbs"
                      >
                        <BreadcrumbEllipsis />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <RouterLink
                        v-for="(crumb, index) in breadcrumbDisplay.overflow"
                        :key="index"
                        :to="crumb.path"
                      >
                        <DropdownMenuItem>
                          {{ crumb.title }}
                        </DropdownMenuItem>
                      </RouterLink>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </BreadcrumbItem>
                <BreadcrumbSeparator class="hidden md:block" />

                <template
                  v-for="(crumb, index) in breadcrumbDisplay.end"
                  :key="index"
                >
                  <BreadcrumbItem>
                    <RouterLink
                      v-if="index !== breadcrumbs.length - 1"
                      :to="crumb.path"
                    >
                      <BreadcrumbLink>
                        {{ crumb.title }}
                      </BreadcrumbLink>
                    </RouterLink>

                    <BreadcrumbPage v-else>
                      {{ crumb.title }}
                    </BreadcrumbPage>
                  </BreadcrumbItem>

                  <BreadcrumbSeparator
                    v-if="index !== breadcrumbDisplay.end.length - 1"
                    class="hidden md:block"
                  />
                </template>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          <div v-if="!useAuthStore().isSuperAdmin">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <RouterLink :to="dashboardRoute">
                    <BreadcrumbLink>
                      Dashboard
                    </BreadcrumbLink>
                  </RouterLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator v-if="route.meta.pageTitle" />
                <BreadcrumbPage>
                  {{ route.meta.pageTitle }}
                </BreadcrumbPage>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          <div
            id="header-slot"
            class="ms-auto"
          />

          <div class="flex gap-3 items-center">
            <QuickFeedbackPopover />
            <NotificationDropdown />
            <ThemeToggler />
          </div>
        </div>
      </header>

      <div
        class="flex min-h-0 flex-1"
        :class="
          showDashboardInsights
            ? 'flex-col overflow-auto xl:flex-row xl:overflow-hidden'
            : 'flex-col gap-6 overflow-auto p-6 mr-1'
        "
      >
        <div
          class="min-w-0"
          :class="
            showDashboardInsights
              ? 'flex-1 space-y-4 p-6 xl:overflow-auto'
              : 'flex w-full flex-1 flex-col gap-6'
          "
        >
          <RouterView />
        </div>

        <aside
          v-if="showDashboardInsights"
          class="flex w-full shrink-0 flex-col border-t bg-muted/40 xl:h-full xl:w-[20rem] xl:border-l xl:border-t-0 xl:overflow-hidden"
          aria-label="Insights and action center"
        >
          <DashboardInsightsSidebar />
        </aside>
      </div>
    </SidebarInset>
  </SidebarProvider>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue'
import { useRoute, useRouter } from "vue-router";
import { ArrowLeft } from "lucide-vue-next";
import { useAuthStore, useWorkspaceStore } from "@/stores/index.js";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@/components/ui/breadcrumb'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { BreadcrumbEllipsis } from "@/components/ui/breadcrumb/index.js";
import ThemeToggler from "@/components/common/ThemeToggler.vue";
import NotificationDropdown from "@/components/common/NotificationDropdown.vue";
import QuickFeedbackPopover from "@/components/feedbacks/QuickFeedbackPopover.vue";
import AppSidebar from '@/components/AppSidebar.vue'
import { useDashboardModules } from '@/queries/dashboard'

// Only rendered on the workspace dashboard; lazy so its calendar/date libs
// stay out of the bundle every other page loads.
const DashboardInsightsSidebar = defineAsyncComponent(
  () => import('@/components/dashboard/DashboardInsightsSidebar.vue'),
)

const { permittedModules } = useDashboardModules()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const MAX_VISIBLE = 3

/** Full-height insights rail on the workspace dashboard (hidden for audit assignees). */
const showDashboardInsights = computed(
  () =>
    route.name === 'workspaces-dashboard' &&
    !authStore.isAuditAssignee,
)

const dashboardRoute = computed(() => {
  const workspaceId = route.params.workspace_id || workspaceStore.getWorkspaceID
  return workspaceId
    ? { name: 'workspaces-dashboard', params: { workspace_id: workspaceId } }
    : { name: 'workspaces' }
})

const isModuleDisabled = computed(() => {
  const moduleName = route.meta.module
  if (!moduleName) return false

  const module = (permittedModules.value).find((m) => m.name === moduleName)
  if (!module) return false

  return !module.workspace_is_enabled
})

const hasPreviousRoute = computed(() => {
  // force dependency on route
  void route.path
  return window.history.state.back !== null
})



const breadcrumbs = computed(() => {
  return route.matched
    .filter(r => r.meta?.title)
    .map(r => {
      let resolvedPath = r.path

      Object.entries(route.params).forEach(([key, value]) => {
        const paramValue = Array.isArray(value) ? value[0] : value
        resolvedPath = resolvedPath.replace(`:${key}`, paramValue ?? '')
      })

      return {
        key: r.name,
        title: r.meta.title,
        path: resolvedPath
      }
    })
})

const breadcrumbDisplay = computed(() => {
  const all = breadcrumbs.value

  if (all.length <= MAX_VISIBLE) {
    return {
      start: all,
      overflow: [],
      end: []
    }
  }

  return {
    start: all.slice(0, 1),
    overflow: all.slice(1, -2),
    end: all.slice(-2)
  }
})

const goBack = () => {
  router.back()
}



</script>
